import React, { useState, useEffect, useMemo } from "react";
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf, faFileExcel, faPrint } from '@fortawesome/free-solid-svg-icons';
import './ClientsList.css';

import useTableSelection from './shared/useTableSelection';
import TableToolbar from './shared/TableToolbar';
import { exportToPrint, exportToExcel, exportToPDF } from './shared/exportTable';

const ClientsList = ({ openEditDrawer, refreshTrigger, filter = 'all' }) => {
    const [clientsData, setClientsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    const userType = localStorage.getItem("usertype");
    const loggedInUserId = localStorage.getItem("id") || localStorage.getItem("user_id");

    const fetchClients = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            const res = await axios.get('https://skcapi.btcjapan.co/get_clients.php', {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            if (res.data.success) {
                let fetchedClients = res.data.data;

                if (userType === 'administrator') {
                    // Admin sees all
                } else if (userType === 'leader') {
                    let leadersGroupId = null;
                    for (const client of fetchedClients) {
                        if (client.assigned_users && Array.isArray(client.assigned_users)) {
                            const found = client.assigned_users.find(u => String(u.id) === String(loggedInUserId));
                            if (found) {
                                leadersGroupId = client.group_name;
                                break;
                            }
                        }
                    }
                    if (leadersGroupId) {
                        fetchedClients = fetchedClients.filter(client => client.group_name === leadersGroupId);
                    } else {
                        fetchedClients = [];
                    }
                } else if (userType === 'member') {
                    fetchedClients = fetchedClients.filter(client => 
                        client.assigned_users && Array.isArray(client.assigned_users) &&
                        client.assigned_users.some(u => String(u.id) === String(loggedInUserId))
                    );
                }

                setClientsData(fetchedClients);
            } else {
                setClientsData([]);
            }
        } catch (err) { 
            console.error("Fetch Error:", err); 
            setClientsData([]); 
        } finally { 
            setLoading(false); 
        }
    };

    useEffect(() => {
        fetchClients();
    }, [refreshTrigger]);

    const handleDelete = async (id) => {
        if (userType !== 'administrator') {
            alert("Only Super Admin can delete clients.");
            return;
        }
        if (!window.confirm("Are you sure you want to delete this client?")) return;
        try {
            const token = localStorage.getItem("token");
            await axios.post('https://skcapi.btcjapan.co/delete_client.php', { id }, { headers: { Authorization: `Bearer ${token}` } });
            fetchClients();
        } catch (err) {
            alert("Error deleting client");
            console.error(err);
        }
    };

    // ==================== APPLY COUNTRY FILTER + SEARCH ====================
    const filteredClients = useMemo(() => {
        // Step 1 — apply country filter from Client.js
        let result = clientsData;
        if (filter && filter !== 'all') {
            result = result.filter(client =>
                (client.client_country || '').toLowerCase() === filter.toLowerCase()
            );
        }

        // Step 2 — apply search on top
        const term = searchTerm.toLowerCase();
        if (!term) return result;

        return result.filter(client =>
            (client.client_name || "").toLowerCase().includes(term) ||
            (client.client_email || "").toLowerCase().includes(term) ||
            (client.client_phone || "").includes(term)
        );
    }, [clientsData, filter, searchTerm]);

     // ============================================================
    // [2] SELECTION HOOK — one line, uses filteredClients
    // ============================================================
    const selection = useTableSelection(filteredClients, (row) => row.id);

    // ============================================================
    // [3] EXPORT CONFIG — column headers + row builder + handler
    // ============================================================

    const exportColumns = [
        'Name',
        'Contacts',
        'Primary Contact',
        'Stock',
        'Due Amount',
        'Received Amount',
        'Expense',
        'Wallet Amount',
        'Group',
        'Sale Person',
    ];

    const buildExportRows = (rows) => rows.map(client => [
        client.client_name || '',
        client.client_phone || '',
        client.primary_contact || '',
        client.stock || '',
        (Number(client.due_amount) || 0).toFixed(2),
        (Number(client.received_amount) || 0).toFixed(2),
        (Number(client.expense) || 0).toFixed(2),
        (Number(client.wallet_amount) || 0).toFixed(2),
        client.group_name || '',
        client.sale_person || '',
    ]);

    const handleExport = (kind) => {
        const rows = selection.selectedRows;
        if (rows.length === 0) return;
        const title = 'Clients Export';
        const data = buildExportRows(rows);
        if (kind === 'print') exportToPrint(title, exportColumns, data);
        if (kind === 'excel') exportToExcel(title, exportColumns, data);
        if (kind === 'pdf')   exportToPDF(title, exportColumns, data);
    };

    return (
        <div className="clients-list-container">
            {/* ============================================================ */}
            {/* [4] TOOLBAR — replace the entire <div className="toolbar">    */}
            {/*     block with this single component.                         */}
            {/* ============================================================ */}
            <TableToolbar
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                isSelectMode={selection.isSelectMode}
                onToggleSelectMode={selection.toggleSelectMode}
                onSelectAll={() =>
                    selection.allSelected ? selection.clearSelection() : selection.selectAll()
                }
                selectedCount={selection.selectedIds.size}
                allSelected={selection.allSelected}
                onPrint={() => handleExport('print')}
                onExcel={() => handleExport('excel')}
                onPDF={() => handleExport('pdf')}
            />

            <div className="table-responsive">
                <table className="leads-table">
                    <thead>
                        <tr>
                            {/* ============================================ */}
                            {/* [5a] CHECKBOX HEADER — only in select mode  */}
                            {/* ============================================ */}
                            {selection.isSelectMode && (
                                <th style={{ width: 42, textAlign: 'center' }}>
                                    <input
                                        type="checkbox"
                                        checked={selection.allSelected}
                                        onChange={() =>
                                            selection.allSelected
                                                ? selection.clearSelection()
                                                : selection.selectAll()
                                        }
                                        title={selection.allSelected ? 'Unselect All' : 'Select All'}
                                    />
                                </th>
                            )}
                            <th>Name</th>
                            <th>Contacts</th>
                            <th>Primary Contact</th>
                            <th>Stock</th>
                            <th>Due Amount</th>
                            <th>Received Amount</th>
                            <th>Expense</th>
                            <th>Wallet Amount</th>
                            <th>Group</th>
                            <th>Sale Person</th>
                            {/* 
                            <th>Action</th>
                            */}
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="11" style={{textAlign:"center", padding: 40, color:"#888"}}>Loading clients...</td></tr>
                        ) : filteredClients.length === 0 ? (
                            <tr>
                                <td colSpan="11" style={{textAlign:"center", padding: 40, color:"#888"}}>
                                    {clientsData.length === 0
                                        ? "No clients found."
                                        : "No clients match the current filter."}
                                </td>
                            </tr>
                        ) : (
                            filteredClients.map(client => (
                                <tr key={client.id}>
                                    {/* ============================================ */}
                                    {/* [5b] CHECKBOX CELL — only in select mode    */}
                                    {/* ============================================ */}
                                    {selection.isSelectMode && (
                                        <td
                                            style={{ textAlign: 'center' }}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selection.isRowSelected(client.id)}
                                                onChange={() => selection.toggleRow(client.id)}
                                            />
                                        </td>
                                    )}
                                    <td>{client.client_name}</td>
                                    <td>{client.client_phone}</td>
                                    <td>{client.primary_contact}</td>
                                    <td>{client.stock}</td>
                                    <td>{(Number(client.due_amount) || 0).toFixed(2)}</td>
                                    <td>{(Number(client.received_amount) || 0).toFixed(2)}</td>
                                    <td>{(Number(client.expense) || 0).toFixed(2)}</td>
                                    <td>{(Number(client.wallet_amount) || 0).toFixed(2)}</td>
                                    <td>{client.group_name}</td>
                                    <td>{client.sale_person}</td>
                                    {/* 
                                    <td>
                                        <div className="action-btns">
                                            <button className="action-icon-btn edit" onClick={() => openEditDrawer(client.id)}>✎</button>
                                            <button 
                                                className="action-icon-btn delete" 
                                                onClick={() => handleDelete(client.id)}
                                                disabled={userType !== 'administrator'}
                                            >
                                                🗑
                                            </button>
                                        </div>
                                    </td>
                                    */}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ClientsList;